import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "@/data/store";
import { onContinueExam } from "@/features/assignment";
import { submit } from "@/features/authority";
import { SubmissionType } from "@/generated/graphql";

/** Materials redux state */
export interface LayoutState {
  /** Open state of the left Materials pane */
  isMaterialsOpen: boolean;
  /** Currently selected tab to render in the panes */
  materialsTab: MaterialsTab;
  /** Open editor track for the body editor on the right. */
  isTrackOpen: boolean;
}

/**
 * Various selectable tabs to determine the contents of the Materials panes.
 * Materials sit adjacent to the main Student Work.
 */
export enum MaterialsTab {
  Instructions = "instructions",
  Notes = "notes",
  Draft = "draft",
  Final = "final",
  Files = "files",
}

const initialState: LayoutState = {
  isMaterialsOpen: true,
  isTrackOpen: false,
  materialsTab: MaterialsTab.Instructions,
};

export const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    setMaterialsTab: (state, action: PayloadAction<MaterialsTab>) => {
      state.materialsTab = action.payload;
      state.isMaterialsOpen = true;
    },
    openMaterials: (state) => {
      state.isMaterialsOpen = true;
    },
    closeMaterials: (state) => {
      state.isMaterialsOpen = false;
    },
    openTrack: (state) => {
      state.isTrackOpen = true;
    },
    closeTrack: (state) => {
      state.isTrackOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(submit.fulfilled, (state, action) => {
      state.isMaterialsOpen = true;
      if (action.meta.arg?.submissionType === SubmissionType.Draft) {
        state.materialsTab = MaterialsTab.Draft;
      } else {
        state.materialsTab = MaterialsTab.Final;
      }
    });

    builder.addCase(onContinueExam, (state) => {
      state.isMaterialsOpen = true;
      state.materialsTab = MaterialsTab.Instructions;
    });
  },
});

// Export Actions
export const {
  setMaterialsTab,
  openMaterials,
  closeMaterials,
  openTrack,
  closeTrack,
} = layoutSlice.actions;

// Export Reducer
export const layoutReducer = layoutSlice.reducer;

// Selectors
export function selectMaterialsOpen(state: RootState) {
  return state.layout.isMaterialsOpen;
}

export function selectMaterialsTab(state: RootState) {
  return state.layout.materialsTab;
}

export function selectLayoutState(state: RootState) {
  return state.layout;
}

export function selectTrackOpen(state: RootState) {
  return state.layout.isTrackOpen;
}
